import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Stack,
    Box,
    Chip,
    Grid,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Info as InfoIcon,
    Timeline as TimelineIcon,
} from '@mui/icons-material';

// Utility functions
const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
        case 'high': return 'error';
        case 'medium': return 'warning';
        case 'low': return 'info';
        default: return 'default';
    }
};

const getAlertTypeColor = (alertType) => {
    if (alertType?.includes('WASH_TRADE')) return 'error';
    if (alertType?.includes('SPOOFING')) return 'warning';
    if (alertType?.includes('LAYERING')) return 'info';
    if (alertType?.includes('PUMP') || alertType?.includes('DUMP') || alertType?.includes('RAID')) return 'error';
    return 'default';
};

const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IN').format(num);
};

const SuspiciousTickDetailsModal = ({ open, onClose, tick }) => {
    const theme = useTheme();
    const [expandedAccordion, setExpandedAccordion] = useState('basic');

    if (!tick) return null;

    // Parse JSON data
    let marketContext = {};
    let orderBookSnapshot = {};

    try {
        marketContext = tick.marketContext ? JSON.parse(tick.marketContext) : {};
    } catch (e) {
        console.error('Error parsing marketContext:', e);
    }

    try {
        orderBookSnapshot = tick.orderBookSnapshot ? JSON.parse(tick.orderBookSnapshot) : {};
    } catch (e) {
        console.error('Error parsing orderBookSnapshot:', e);
    }

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedAccordion(isExpanded ? panel : false);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minHeight: '80vh' }
            }}
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="h6">
                            Suspicious Tick Details
                        </Typography>
                        <Chip
                            label={tick.severityLevel?.toUpperCase()}
                            color={getSeverityColor(tick.severityLevel)}
                            size="small"
                        />
                    </Stack>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {/* Basic Information */}
                <Accordion 
                    expanded={expandedAccordion === 'basic'} 
                    onChange={handleAccordionChange('basic')}
                    defaultExpanded
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ bgcolor: 'background.neutral' }}
                    >
                        <Typography variant="subtitle1" fontWeight="bold">
                            <InfoIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                            Basic Information
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Instrument Details
                                    </Typography>
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Script:</Typography>
                                            <Typography variant="body1" fontWeight="medium">{tick.script}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Segment:</Typography>
                                            <Typography variant="body1">{tick.segment}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Price:</Typography>
                                            <Typography variant="h6" color="primary.main">{tick.priceFormatted}</Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Alert Details
                                    </Typography>
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Alert Type:</Typography>
                                            <Chip
                                                label={tick.alertType.replace(/_/g, ' ')}
                                                color={getAlertTypeColor(tick.alertType)}
                                                variant="outlined"
                                                size="small"
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Timestamp:</Typography>
                                            <Typography variant="body1">{tick.timestampFormatted}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Direction:</Typography>
                                            <Typography variant="body1">{tick.direction || 'N/A'}</Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.neutral' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Description
                                    </Typography>
                                    <Typography variant="body1">{tick.description}</Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                {/* Market Metrics */}
                <Accordion 
                    expanded={expandedAccordion === 'metrics'} 
                    onChange={handleAccordionChange('metrics')}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ bgcolor: 'background.neutral' }}
                    >
                        <Typography variant="subtitle1" fontWeight="bold">
                            <TimelineIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                            Market Metrics
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Box textAlign="center" sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <Typography variant="h6" color="primary.main">
                                        {formatNumber(tick.volume)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Volume
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box textAlign="center" sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <Typography variant="h6" color="secondary.main">
                                        {tick.priceImpact?.toFixed(6) || '0.000000'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Price Impact
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box textAlign="center" sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <Typography variant="h6" color="info.main">
                                        {tick.volumeDelta?.toFixed(6) || '0.000000'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Volume Delta
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                {/* Market Context */}
                {Object.keys(marketContext).length > 0 && (
                    <Accordion 
                        expanded={expandedAccordion === 'context'} 
                        onChange={handleAccordionChange('context')}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ bgcolor: 'background.neutral' }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                <TrendingUpIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                                Market Context Analysis
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {marketContext.AverageVolume && (
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="h6" color="primary.main">
                                                {parseFloat(marketContext.AverageVolume).toFixed(4)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Average Volume
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                                {marketContext.CurrentSpread && (
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="h6" color="secondary.main">
                                                {parseFloat(marketContext.CurrentSpread).toFixed(6)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Current Spread
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                                {marketContext.LiquidityScore && (
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="h6" color="info.main">
                                                {marketContext.LiquidityScore}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Liquidity Score
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                                {marketContext.BidAskImbalance && (
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="h6" color="warning.main">
                                                {parseFloat(marketContext.BidAskImbalance).toFixed(4)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Bid-Ask Imbalance
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                                {marketContext.RecentTradeCount && (
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="h6" color="success.main">
                                                {marketContext.RecentTradeCount}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Recent Trade Count
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                                {marketContext.CurrentVolatility && (
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="h6" color="error.main">
                                                {parseFloat(marketContext.CurrentVolatility).toFixed(8)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Current Volatility
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Order Book Snapshot */}
                {(orderBookSnapshot.BidLevels || orderBookSnapshot.AskLevels) && (
                    <Accordion 
                        expanded={expandedAccordion === 'orderbook'} 
                        onChange={handleAccordionChange('orderbook')}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ bgcolor: 'background.neutral' }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                <TrendingDownIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                                Order Book Snapshot
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={3}>
                                {/* Ask Levels */}
                                {orderBookSnapshot.AskLevels && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="error.main" gutterBottom>
                                            Ask Levels (Sell Orders)
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Price</TableCell>
                                                        <TableCell align="right">Quantity</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {orderBookSnapshot.AskLevels.map((level, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <Typography variant="body2" color="error.main">
                                                                    {level.price}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body2">
                                                                    {level.quantity}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                )}

                                {/* Bid Levels */}
                                {orderBookSnapshot.BidLevels && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="success.main" gutterBottom>
                                            Bid Levels (Buy Orders)
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Price</TableCell>
                                                        <TableCell align="right">Quantity</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {orderBookSnapshot.BidLevels.map((level, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <Typography variant="body2" color="success.main">
                                                                    {level.price}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body2">
                                                                    {level.quantity}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                )}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Warning Message */}
                <Box sx={{ mt: 2 }}>
                    <Alert severity="info" icon={<InfoIcon />}>
                        <Typography variant="body2">
                            This analysis is generated by automated algorithms. Please conduct thorough investigation 
                            before taking any regulatory or trading action based on this alert.
                        </Typography>
                    </Alert>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

SuspiciousTickDetailsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    tick: PropTypes.object,
};

export default SuspiciousTickDetailsModal;
